import { useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faEllipsis,
} from "@fortawesome/free-solid-svg-icons";

import Button from "components/Button";
import ConfirmModal from "components/ConfirmModal";
import Loading from "components/Loading";

import councilService from "api/councilService";
import { ECouncilSessionState, ICouncilSession } from "dto/Council/ICouncil";
import { ENotificationType, notification } from "utils/notification";
import { useOnClickOutside } from "usehooks-ts";

import AgendaEditor from "./AgendaEditor";
import ProtocolModal from "./ProtocolModal";
import SessionMetricModal from "./SessionMetricModal";
import { decodeSession } from "./decodeText";
import styles from "./styles.module.scss";

// Biuro rady → „Sesja". Odwzorowanie Kwantum.Cases.UI.CouncilSessionAgenda.View.SessionList:
// w pasku górnym rozwijana lista sesji bieżących (z przyciskiem „Nowa sesja"), po prawej
// menu „Więcej…", a pod spodem porządek obrad wybranej sesji.
//
// Lista pokazuje sesje nierozpoczęte i trwające (State < Finished) i nie zawiera
// archiwalnych — te są w widoku „Archiwum sesji".

const formatDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString("pl-PL") : "";

const stateLabel = (session: ICouncilSession) => {
  if (session.State === ECouncilSessionState.Suspended) return "zawieszona";
  if (session.State === ECouncilSessionState.Finished) return "zakończona";
  if (session.IsActive) return "opublikowana";
  return "";
};

const errorText = (e: unknown, fallback: string) =>
  (e as { response?: { data?: { Message?: string } } })?.response?.data?.Message ??
  fallback;

const SessionView = () => {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [metricFor, setMetricFor] = useState<{ session: ICouncilSession | null } | null>(
    null,
  );
  const [confirm, setConfirm] = useState<"archive" | "remove" | null>(null);
  const [protocolFor, setProtocolFor] = useState<ICouncilSession | null>(null);

  const pickerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useOnClickOutside(pickerRef, () => setPickerOpen(false));
  useOnClickOutside(menuRef, () => setMenuOpen(false));

  const sessionsQuery = councilService.useGetSessions({ archived: false });
  const { mutate: publish } = councilService.usePublishSession();
  const { mutate: archive } = councilService.useArchiveSession();
  const { mutate: remove } = councilService.useRemoveSession();

  // Widok „Sesja" pokazuje tylko sesje jeszcze niezakończone — jak filtr
  // `State < Finished` w SessionsViewModel.OnLoading.
  const sessions = useMemo(
    () =>
      (sessionsQuery.data?.data ?? [])
        .filter((s) => s.State < ECouncilSessionState.Finished)
        .map(decodeSession),
    [sessionsQuery.data],
  );

  // Wybór domyślny jak w Magistracie: trwająca/zawieszona → opublikowana → pierwsza.
  useEffect(() => {
    if (!sessions.length) {
      setSelectedId(null);
      return;
    }

    if (selectedId && sessions.some((s) => s.Id === selectedId)) return;

    const inProgress = sessions.find(
      (s) =>
        s.State === ECouncilSessionState.InProgress ||
        s.State === ECouncilSessionState.Suspended,
    );
    const active = sessions.find((s) => s.IsActive);

    setSelectedId((inProgress ?? active ?? sessions[0]).Id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessions]);

  const selected = sessions.find((s) => s.Id === selectedId) ?? null;

  const reload = (nextId?: string) =>
    sessionsQuery.refetch().then(() => {
      if (nextId) setSelectedId(nextId);
    });

  const doPublish = () => {
    if (!selected) return;
    setMenuOpen(false);

    publish(selected.Id, {
      onSuccess: () => {
        notification({
          type: ENotificationType.SUCCESS,
          title: "Opublikowano",
          text: "Porządek obrad jest dostępny w portalu radnego.",
        });
        reload();
      },
      onError: (e) =>
        notification({
          type: ENotificationType.ERROR,
          title: "Błąd",
          text: errorText(e, "Nie udało się opublikować sesji."),
        }),
    });
  };

  const doArchive = () => {
    if (!selected) return;
    setConfirm(null);

    archive(selected.Id, {
      onSuccess: () => {
        setSelectedId(null);
        reload();
      },
      onError: (e) =>
        notification({
          type: ENotificationType.ERROR,
          title: "Błąd",
          text: errorText(e, "Nie udało się zarchiwizować sesji."),
        }),
    });
  };

  const doRemove = () => {
    if (!selected) return;
    setConfirm(null);

    remove(selected.Id, {
      onSuccess: () => {
        setSelectedId(null);
        reload();
      },
      onError: (e) =>
        notification({
          type: ENotificationType.ERROR,
          title: "Nie można usunąć",
          text: errorText(e, "Nie udało się usunąć sesji."),
        }),
    });
  };

  if (sessionsQuery.isLoading) {
    return <Loading />;
  }

  return (
    <div className={styles.panel}>
      <div className={styles.topBar}>
        <div ref={pickerRef} style={{ position: "relative" }}>
          <div
            className={`${styles.sessionPicker} ${
              selected ? "" : styles.sessionPickerEmpty
            }`}
            onClick={() => setPickerOpen((v) => !v)}
          >
            {selected
              ? `${formatDate(selected.Date)}    ${selected.Number ?? ""}`
              : "Wybierz sesję"}
            <FontAwesomeIcon icon={faChevronDown} className={styles.pickerChevron} />
          </div>

          {pickerOpen && (
            <div className={styles.pickerPopover}>
              <div className={styles.pickerHeader}>
                <Button
                  text="Nowa sesja"
                  onClick={() => {
                    setPickerOpen(false);
                    setMetricFor({ session: null });
                  }}
                />
              </div>

              {sessions.map((session) => (
                <div
                  key={session.Id}
                  className={`${styles.pickerRow} ${
                    session.Id === selectedId ? styles.pickerRowActive : ""
                  }`}
                  onClick={() => {
                    setSelectedId(session.Id);
                    setPickerOpen(false);
                  }}
                >
                  <span className={styles.pickerNumber}>{session.Number}</span>
                  <span className={styles.pickerDate}>{formatDate(session.Date)}</span>
                  <span className={styles.pickerState}>{stateLabel(session)}</span>
                </div>
              ))}

              {!sessions.length && (
                <div className={styles.pickerRow}>
                  <span className={styles.pickerState}>Brak sesji</span>
                </div>
              )}
            </div>
          )}
        </div>

        {selected?.IsActive && (
          <span className={styles.publishedBadge}>opublikowana</span>
        )}

        <div className={styles.spacer} />

        {selected && (
          <div ref={menuRef} style={{ position: "relative" }}>
            <button
              type="button"
              className={styles.moreButton}
              title="Więcej…"
              onClick={() => setMenuOpen((v) => !v)}
            >
              <FontAwesomeIcon icon={faEllipsis} />
            </button>

            {menuOpen && (
              <div className={styles.menuPopover}>
                <button
                  type="button"
                  className={styles.menuItem}
                  onClick={() => {
                    setMenuOpen(false);
                    setMetricFor({ session: selected });
                  }}
                >
                  Edytuj metrykę sesji
                </button>

                {!selected.IsActive && (
                  <button type="button" className={styles.menuItem} onClick={doPublish}>
                    Opublikuj porządek obrad
                  </button>
                )}

                <button
                  type="button"
                  className={styles.menuItem}
                  onClick={() => {
                    setMenuOpen(false);
                    setProtocolFor(selected);
                  }}
                >
                  {selected.HasProtocol ? "Protokół z sesji" : "Dołącz protokół"}
                </button>

                <button
                  type="button"
                  className={styles.menuItem}
                  onClick={() => {
                    setMenuOpen(false);
                    setConfirm("archive");
                  }}
                >
                  Przenieś do archiwum
                </button>

                {selected.State === ECouncilSessionState.NotStarted && (
                  <button
                    type="button"
                    className={`${styles.menuItem} ${styles.menuItemDanger}`}
                    onClick={() => {
                      setMenuOpen(false);
                      setConfirm("remove");
                    }}
                  >
                    Usuń sesję
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {selected ? (
        <AgendaEditor
          key={selected.Id}
          session={selected}
          onSessionChanged={() => reload()}
        />
      ) : (
        <div className={styles.noSession}>
          <div className={styles.noSessionTitle}>Brak bieżącego porządku obrad</div>
          <Button
            text="Utwórz nowy porządek"
            onClick={() => setMetricFor({ session: null })}
          />
        </div>
      )}

      {metricFor && (
        <SessionMetricModal
          session={metricFor.session}
          onClose={() => setMetricFor(null)}
          onSaved={(session) => {
            setMetricFor(null);
            reload(session.Id);
          }}
        />
      )}

      {protocolFor && (
        <ProtocolModal
          session={protocolFor}
          onClose={() => setProtocolFor(null)}
          onSaved={() => {
            setProtocolFor(null);
            reload();
          }}
        />
      )}

      {confirm === "archive" && (
        <ConfirmModal
          title="Zarchiwizować?"
          text="Czy na pewno chcesz zarchiwizować wybraną sesję? Znajdziesz ją potem w widoku „Archiwum sesji”."
          acceptLabel="Zarchiwizuj"
          cancelLabel="Anuluj"
          showDontShowNextTime={false}
          onAccept={doArchive}
          onCancel={() => setConfirm(null)}
        />
      )}

      {confirm === "remove" && (
        <ConfirmModal
          title="Usunąć?"
          text="Czy na pewno chcesz usunąć wybraną sesję wraz z jej porządkiem obrad?"
          acceptLabel="Usuń"
          cancelLabel="Anuluj"
          showDontShowNextTime={false}
          onAccept={doRemove}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
};

export default SessionView;
